import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { planTopic, type TopicPlan } from '@/lib/topic-plan';
import { quickDates, type PlannedTodayRow } from './today-horizons';

/** J5: rolled-over engagements and the backlog, one at a time. */
export const TodayReview = ({ items, onClose }: { items: PlannedTodayRow[]; onClose: () => void }) => {
  const dialog = useRef<HTMLDialogElement>(null);
  const [queue] = useState(items);
  const [index, setIndex] = useState(0);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { tomorrow, monday } = quickDates(new Date());
  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (typeof element.showModal === 'function') element.showModal();
    else element.setAttribute('open', '');
  }, []);
  const current = queue[index];
  const next = () => {
    if (index + 1 >= queue.length) onClose();
    else setIndex(index + 1);
  };
  const plan = async (horizon: TopicPlan, plannedFor: string | null = null) => {
    if (!current) return;
    setPending(true);
    setError(null);
    try {
      await planTopic({
        topicId: current.row.topic.id,
        expectedTopicVersion: current.row.topic.version,
        horizon,
        plannedFor,
      });
      next();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Le travail n’a pas pu être planifié.');
    } finally {
      setPending(false);
    }
  };
  return (
    <dialog
      ref={dialog}
      aria-labelledby="today-review-title"
      onClose={onClose}
      className="w-[min(520px,calc(100vw-32px))] rounded-xl border border-domino-cardBorder bg-domino-white p-5 shadow-xl backdrop:bg-black/20"
    >
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <h2 id="today-review-title" className="text-base font-semibold text-domino-ink">Revue</h2>
        <span className="text-xs tabular-nums text-domino-inkSoft">{Math.min(index + 1, queue.length)} / {queue.length}</span>
      </div>
      {current && (
        <>
          <p className="text-xs text-domino-inkSoft">{current.row.ownerLabel}</p>
          <p className="mt-1 text-sm font-medium leading-6 text-domino-ink">{current.row.topic.title}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button size="sm" disabled={pending} onClick={() => void plan('today')}>Aujourd’hui</Button>
            <Button size="sm" variant="secondary" disabled={pending} onClick={() => void plan('later', tomorrow)}>Demain</Button>
            <Button size="sm" variant="secondary" disabled={pending} onClick={() => void plan('later', monday)}>Lundi</Button>
            {current.bucket !== 'someday' && (
              <Button size="sm" variant="secondary" disabled={pending} onClick={() => void plan('someday')}>Un jour</Button>
            )}
            <Button size="sm" variant="ghost" disabled={pending} onClick={() => void plan('dropped')}>Abandonner</Button>
            <Button size="sm" variant="ghost" disabled={pending} onClick={next}>Passer</Button>
          </div>
          {error && <p role="alert" className="mt-3 text-sm text-red-700">{error}</p>}
        </>
      )}
      <div className="mt-5 flex justify-end">
        <Button size="sm" variant="ghost" onClick={onClose}>Fermer</Button>
      </div>
    </dialog>
  );
};
